import type { AssessmentSubmissionAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import AssessmentQ from './AssessmentQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'

const AssessmentSubmissionQ = {
  ...makeQueryModelV2<AssessmentSubmissionAttributes>('assessment_submission')([
    'id',
    'user_id',
    'assessment_id',
    'series_id',
    'responses',
    'created_at',
    'deleted_at',
  ]),

  shouldShowForSeries: ({
    seriesId,
    seriesCreatedAt,
  }: {
    seriesId: SQLFragment<number>
    seriesCreatedAt: SQLFragment<Date>
  }): SQLFragment<boolean> =>
    SQL`(
      ${AssessmentSubmissionQ.series_id} = ${seriesId}
      OR (${AssessmentSubmissionQ.series_id} IS NULL AND ${AssessmentSubmissionQ.created_at} >= ${seriesCreatedAt})
    )`,

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: AssessmentSubmissionQ.user_id,
    },
    Assessment: {
      query: AssessmentQ,
      foreignKey: AssessmentQ.id,
      sourceKey: AssessmentSubmissionQ.assessment_id,
    },
  }),

  where: (
    {
      id,
      userId,
      assessmentId,
      seriesId,
      paranoid = true,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | SQLFragment<number>
      userId?: number | SQLFragment<number>
      assessmentId?: number | SQLFragment<number>
      seriesId?: number | SQLFragment<number> | null
      paranoid?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<AssessmentSubmissionAttributes> => SQL`
        FROM ${AssessmentSubmissionQ}
        ${includeAssociationsToSql(AssessmentSubmissionQ, include)}
        WHERE
            ${id != null ? SQL`${AssessmentSubmissionQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${AssessmentSubmissionQ.user_id} = ${userId} AND` : SQL``}
            ${
              assessmentId != null
                ? SQL`${AssessmentSubmissionQ.assessment_id} = ${assessmentId} AND`
                : SQL``
            }
            ${
              seriesId !== undefined
                ? seriesId != null
                  ? SQL`${AssessmentSubmissionQ.series_id} = ${seriesId} AND`
                  : SQL`${AssessmentSubmissionQ.series_id} IS NULL AND`
                : SQL``
            }
            ${paranoid ? SQL`${AssessmentSubmissionQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,
}

export default AssessmentSubmissionQ
